import { useState, useEffect } from 'react';
import { db, storage } from '../../config/FirebaseConfig';
import { collection, addDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { FiPlus, FiTrash2, FiX, FiUpload } from 'react-icons/fi';

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    link: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const clientsCollection = collection(db, 'clients');
      const snapshot = await getDocs(clientsCollection);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClients(data);
    } catch (error) {
      console.error('Error al obtener clientes:', error);
      setError(`Error al cargar clientes: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('Archivo seleccionado para cliente:', file.name, file.type, file.size);

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      alert('La imagen no debe superar 3MB');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !formData.name.trim()) {
      setError('Por favor completa todos los campos y selecciona un logo');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      console.log('Iniciando carga de logo...');
      
      // Upload file to Storage
      const timestamp = Date.now();
      const storageRef = ref(storage, `clients/${timestamp}-${selectedFile.name}`);
      console.log('Subiendo archivo a Storage:', storageRef.fullPath);
      await uploadBytes(storageRef, selectedFile);
      console.log('Archivo subido, obteniendo URL...');
      const downloadURL = await getDownloadURL(storageRef);

      // Save to Firestore
      console.log('Guardando documento en Firestore...');
      await addDoc(collection(db, 'clients'), {
        logo: downloadURL,
        name: formData.name,
        link: formData.link,
        storagePath: `clients/${timestamp}-${selectedFile.name}`,
        createdAt: new Date(),
      });

      console.log('Cliente guardado correctamente');
      setError(null);
      alert('Cliente agregado correctamente');
      setFormData({ name: '', link: '' });
      setSelectedFile(null);
      setPreview(null);
      setShowForm(false);
      fetchClients();
    } catch (error) {
      console.error('Error completo:', error);
      const errorMessage = error.message || 'Error desconocido';
      setError(`Error al agregar el cliente: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, storagePath) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este cliente?')) return;

    try {
      setLoading(true);

      // Delete from Storage
      if (storagePath) {
        try {
          const fileRef = ref(storage, storagePath);
          await deleteObject(fileRef);
        } catch (storageError) {
          console.error('Error al eliminar del storage:', storageError);
        }
      }

      // Delete from Firestore
      await deleteDoc(doc(db, 'clients', id));
      alert('Cliente eliminado correctamente');
      fetchClients();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Gestión de Logos de Clientes
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
            Administra los logos que aparecen en la sección de clientes
          </p>
        </div>
        <button
          onClick={() => {
            console.log('Botón Agregar Cliente presionado (Clientes)');
            setShowForm(!showForm);
            if (showForm) {
              setFormData({ name: '', link: '' });
              setSelectedFile(null);
              setPreview(null);
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors"
        >
          <FiPlus size={20} />
          Agregar Cliente
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Nuevo Cliente
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                setFormData({ name: '', link: '' });
                setSelectedFile(null);
                setPreview(null);
              }}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            >
              <FiX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Logo
              </label>
              <div
                className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center hover:border-orange-500 transition-colors cursor-pointer"
                onClick={() => document.getElementById('logoInput')?.click()}
              >
                {preview ? (
                  <div className="space-y-4">
                    <img
                      src={preview}
                      alt="preview"
                      className="max-h-32 mx-auto"
                    />
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {selectedFile?.name}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setPreview(null);
                      }}
                      className="text-sm text-red-600 dark:text-red-400 hover:underline"
                    >
                      Cambiar logo
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FiUpload size={32} className="mx-auto opacity-50" />
                    <p className="text-slate-900 dark:text-white font-semibold">
                      Haz clic o arrastra el logo aquí
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      PNG, JPG, SVG (máx. 3MB)
                    </p>
                  </div>
                )}
                <input
                  id="logoInput"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Nombre del Cliente
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre de la empresa"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Enlace (opcional)
              </label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://ejemplo.com"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ name: '', link: '' });
                  setSelectedFile(null);
                  setPreview(null);
                }}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                onClick={() => console.log('Enviar formulario Clientes presionado')}
                disabled={uploading || !selectedFile}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-400 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {uploading ? 'Agregando...' : 'Agregar Cliente'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Clients Grid */}
      {loading ? (
        <div className="text-center py-8 text-slate-600 dark:text-slate-400">
          Cargando clientes...
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
          <p className="text-slate-600 dark:text-slate-400 mb-4">No hay clientes agregados aún</p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors"
          >
            <FiPlus size={20} />
            Agregar primer cliente
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {clients.map((client) => (
            <div
              key={client.id}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-lg transition-shadow"
            >
              <div className="relative pb-24 bg-slate-100 dark:bg-slate-700 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                <img
                  src={client.logo}
                  alt={client.name}
                  className="w-20 h-20 object-contain"
                />
                <button
                  onClick={() => handleDelete(client.id, client.storagePath)}
                  disabled={loading}
                  className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded transition-colors disabled:opacity-50"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                {client.name}
              </h3>
              {client.link && (
                <a
                  href={client.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate block mt-1"
                >
                  {client.link}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminClients;
